<template>
  <v-card-text>
    <v-form ref="form" v-model="valid" class="add-edit-form">
      <v-select
        v-model="productTypeSelectorValue"
        :items="productTypes"
        placeholder="Select Product Type"
        @update:modelValue="selectProductType"
        v-if="productTypes.length > 1"
        data-testid="ct-products-create-type"
      >
      </v-select>

      <v-text-field
        v-if="!productNameAutoComplete"
        v-model="productSelectorValue"
        label="Enter Product Name"
        :rules="[validateNotEmpty]"
        @update:modelValue="selectOrCreateProduct"
        data-testid="ct-products-create-name"
      />
      <!-- 
      <v-combobox
        v-if="productNameAutoComplete"
        ref="productSelector"
        v-model="productSelectorValue"
        placeholder="Select Product Type"
        item-title="name"
        :items="products"
        :loading="productsPending"
        :rules="[productValidator]"
        @change="selectOrCreateProduct"
        autofocus
        return-object
      > -->
      <template v-slot:item="data">
        <v-list-item-avatar>
          <v-img
            height="45"
            contain
            v-bind:ref="'preview' + parseInt(data.item._jv.id)"
            :src="getImage(data.item.default_image_uuid)"
          />
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title v-html="data.item.name"></v-list-item-title>
          <v-list-item-subtitle
            v-html="data.item.product_type.name"
          ></v-list-item-subtitle>
        </v-list-item-content>
      </template>

      <template v-slot:no-data>
        <v-col v-if="productsPending"> Loading... Please wait </v-col>

        <v-list-item v-else>
          <v-list-item-avatar>
            <v-img height="45" contain :src="getImage(null)" />
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>
              {{
                $refs.productSelector.$refs.input.value || "Start typing a name"
              }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="productNameAutoComplete">
              This product will be created when you click "Next"
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
      <!-- </v-combobox> -->

      <v-combobox
        v-if="isSmartSupplier"
        v-model="skuSelection"
        :items="skuSearchResults"
        :loading="skuSearchLoading"
        item-title="name"
        item-value="sku"
        return-object
        label="Supplier SKU"
        placeholder="Type to search SanMar SKUs..."
        :rules="[validateNotEmpty]"
        :disabled="editingDisabled"
        @update:search="onSkuSearch"
        no-filter
        data-testid="ct-products-create-sku"
      >
        <template v-slot:no-data>
          <v-list-item v-if="skuSearchLoading">
            <v-list-item-title>Searching...</v-list-item-title>
          </v-list-item>
          <v-list-item v-else-if="skuSearchQuery && skuSearchQuery.length >= 2">
            <v-list-item-title>No matching SKUs found</v-list-item-title>
          </v-list-item>
          <v-list-item v-else>
            <v-list-item-title>Type at least 2 characters to search</v-list-item-title>
          </v-list-item>
        </template>
      </v-combobox>

      <v-text-field
        v-else
        v-model="product.item_number"
        label="Supplier SKU"
        :rules="[validateNotEmpty]"
        :disabled="editingDisabled"
        data-testid="ct-products-create-sku"
      />
      <!-- <v-select
        class="mr-2"
        label="Product Tax Category"
        :rules="[validateNotEmpty]"
        :items="productTaxCodeLabels"
        v-model="productTaxCodeLabel"
        :disabled="editingDisabled"
        @change="changedTaxCodeLabel"
        data-testid="ct-products-create-ptc"
      /> -->
      <v-select
        label="Supplier"
        v-model="selectedSupplierId"
        :items="possibleSuppliers"
        placeholder="Select Supplier"
        item-title="name"
        item-value="id"
        :rules="[validateNotEmpty]"
        v-if="possibleSuppliers.length > 0"
        data-testid="ct-products-create-type"
      >
        <template v-slot:append>
          <div v-if="selectedSupplier && selectedSupplier.is_smart_supplier">
            <v-btn variant="flat" size="small" rounded="pill">
              <v-icon color="amber" icon="bolt" />

              <div class="mr-3">Smart Supplier</div>
            </v-btn>
          </div>
        </template>
        <template v-slot:item="{ item, props }">
          <v-list-item v-bind="props">
            <template v-slot:title>
              <div class="d-flex align-center">
                <span>{{ item.raw.name }}</span>
                <v-btn v-if="item.raw.is_smart_supplier" variant="flat" size="small" rounded="pill" class="ml-2">
                  <v-icon color="amber" icon="bolt" />
                  <span class="mr-3">Smart Supplier</span>
                </v-btn>
              </div>
            </template>
          </v-list-item>
        </template>
      </v-select>
    </v-form>
  </v-card-text>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { mapGetters } from "vuex";
import helpers from "@/mixins/helpers";
import rules from "@/mixins/rules";
import Product from "@/models/Product";
import { isRequestCancelled, extractSkuFromQuery } from "@/utils/sku-search";

export default {
  props: {
    initialProduct: {
      type: Object,
      default() {
        return Product.generateEmpty();
      },
    },

    initialDistributor: {
      type: Object,
      required: false,
    },

    productValidator: {
      type: Function,
      default() {
        return true;
      },
    },

    editExistingDisabled: {
      type: Boolean,
      default: false,
    },

    productTypeDisabled: {
      type: Boolean,
      default: false,
    },

    distributorDisabled: {
      type: Boolean,
      default: false,
    },

    productNameAutoComplete: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [helpers, rules],

  data() {
    return {
      valid: null,

      product: Product.generateEmpty(),

      selectedProductType: null,
      possibleProductTypes: [],
      possibleProductTypesPending: true,

      selectedDistributor: {},
      possibleDistributors: [],
      possibleDistributorsPending: true,

      products: [],
      productSelectorValue: null,
      productTypeSelectorValue: null,
      productsPending: true,
      productSelectorValid: false,

      productTaxCodeLabel: "",
      possibleVendors: [],
      selectedSupplier: null,
      selectedSupplierId: null,
      isSmartProduct: false,

      // SanMar SKU autocomplete
      skuSelection: null,
      skuSearchResults: [],
      skuSearchLoading: false,
      skuSearchQuery: "",
      skuSearchTimer: null,
      skuSearchGeneration: 0,
    };
  },

  computed: {
    ...mapGetters([
      "isSuperAdmin",
      "isDistributorManager",
      "isSalesExecutive",
      "getProductTaxCodes",
    ]),

    productTaxCodeLabels() {
      let types = [];
      for (const productType in this.getProductTaxCodes) {
        if (Object.hasOwnProperty.call(this.getProductTaxCodes, productType)) {
          types.push(productType);
        }
      }
      return types;
    },

    editingDisabled() {
      if (this.editExistingDisabled && this.product && this.product._jv.id) {
        return true;
      }

      return false;
    },

    productTypes() {
      let productTypes = [];
      this.possibleProductTypes.forEach((type) => {
        productTypes.push(type.name);
      });
      if (!this.selectedProductType) {
        this.productTypeSelectorValue = productTypes[0];
        const [selectedProdType] = this.possibleProductTypes.filter(
          (productType) => productType.name === this.productTypeSelectorValue
        );
        this.selectedProductType = selectedProdType;
      }
      return productTypes;
    },

    // BROKEN ON PURPOSE, AND LEFT BROKEN -- read before "fixing" it.
    //
    // ``obj.product_type`` no longer exists. A vendor has no product type
    // (Jake, 2026-09-01) and ``/vendor/getVendorList/`` stopped
    // serializing the key, so this comparison is now
    // ``undefined === "<name>"`` -- always false -- and this list is
    // always empty.
    //
    // It is NOT repaired here because the right repair is to delete the
    // product-type gate entirely, and that is a decision about this
    // component's future rather than about the vendor data model. The
    // component's reachability is under separate investigation: it is
    // routed at /products/add and /products/edit/:id but has ZERO inbound
    // navigation anywhere in the frontend, and the picker Jake actually
    // uses is SupplierSkuPanel.vue (see its entitlement spec for the full
    // history). Silently rewriting a filter in a component nobody has
    // established is live would be a guess dressed as a fix.
    //
    // Behavior change: where this previously offered suppliers matching
    // the selected product type, it now offers none. Nothing else in the
    // component throws.
    possibleSuppliers() {
      if (!this.selectedProductType) return [];
      return this.possibleVendors
        .filter(
          (obj) =>
            obj.is_supplier &&
            obj.product_type === this.selectedProductType.name
        )
        .map((s) => {
          // Vuetify 3 item-value needs a flat key for reliable matching.
          // The API returns { _jv: { id: "..." } } — return a new object
          // with a hoisted top-level 'id' to avoid mutating source data.
          if (!s.id && s._jv && s._jv.id) {
            return { ...s, id: s._jv.id };
          }
          return s;
        });
    },

    isSmartSupplier() {
      return !!(this.selectedSupplier && this.selectedSupplier.is_smart_supplier);
    },
  },

  async mounted() {
    if (this.initialDistributor) {
      this.selectedDistributor = this.initialDistributor;
    }

    await this.loadInitialProduct();
    let possibleVendors = await this.loadVendors();
    this.possibleVendors = Object.values(possibleVendors.vendors);

    const isEditMode = !!(this.product._jv && this.product._jv.id);

    if (isEditMode && this._pendingSupplierId && this.possibleSuppliers.length > 0) {
      // Edit mode: resolve the deferred supplier ID against the loaded vendor list.
      // Set _lastSupplierId first so changeSupplier() doesn't reset SKU state.
      const match = this.possibleSuppliers.find(
        (s) => String(s.id) === this._pendingSupplierId
      );
      if (match) {
        this._lastSupplierId = match.id;
        this.selectedSupplier = match;
        this.selectedSupplierId = match.id;
      }
      this._pendingSupplierId = null;

      // Now that supplier is resolved, sync skuSelection
      if (this.isSmartSupplier && this.product.item_number) {
        this.skuSelection = this.product.item_number;
      }
    } else if (!isEditMode) {
      // New product: auto-select a smart supplier
      if (!this.selectedSupplier?.name && this.possibleSuppliers.length > 0) {
        const smartSupplier = this.possibleSuppliers.find(
          (s) => s.is_smart_supplier
        );
        if (smartSupplier) {
          this.selectedSupplier = smartSupplier;
          this.selectedSupplierId = smartSupplier.id;
        }
      }
    }
  },

  beforeUnmount() {
    clearTimeout(this.skuSearchTimer);
    if (this._skuAbortController) {
      this._skuAbortController.abort();
    }
  },

  watch: {
    initialProduct() {
      this.loadInitialProduct();
    },
    selectedSupplierId(id) {
      this.onSupplierIdChanged(id);
    },
    skuSelection(val) {
      this.onSkuSelected(val);
    },
    possibleSuppliers(suppliers) {
      // Auto-select smart supplier (SanMar) when the supplier list first
      // populates — e.g. after user selects a product type. The mounted()
      // auto-select fires too early (before product type is chosen).
      // Only for new products — in edit mode, supplier is restored explicitly.
      const isEditMode = !!(this.product._jv && this.product._jv.id);
      if (!isEditMode && !this.selectedSupplier?.name && suppliers.length > 0) {
        const smartSupplier = suppliers.find((s) => s.is_smart_supplier);
        if (smartSupplier) {
          this.selectedSupplier = smartSupplier;
          this.selectedSupplierId = smartSupplier.id;
        }
      }
    },
  },

  methods: {
    onSupplierIdChanged(id) {
      const supplier =
        this.possibleSuppliers.find((s) => s.id === id) || null;
      this.selectedSupplier = supplier;
      this.changeSupplier(supplier);
    },

    changeSupplier(val) {
      if (val && val.is_smart_supplier) {
        this.$emit("showSmartProductToggle", true);
      } else {
        this.$emit("showSmartProductToggle", false);
      }
      // Only reset SKU state when the supplier actually changed (not on initial load)
      if (this._lastSupplierId !== undefined && this._lastSupplierId !== (val && val.id)) {
        this.skuSelection = null;
        this.skuSearchResults = [];
        this.skuSearchQuery = "";
        this.product.item_number = "";
      }
      this._lastSupplierId = val && val.id;
    },

    onSkuSearch(query) {
      const cleanQuery = extractSkuFromQuery(query);
      this.skuSearchQuery = cleanQuery;
      clearTimeout(this.skuSearchTimer);

      if (cleanQuery.length < 2) {
        this.skuSearchResults = [];
        return;
      }

      this.skuSearchTimer = setTimeout(() => {
        this.fetchSkuResults(cleanQuery);
      }, 300);
    },

    onSkuSelected(val) {
      if (val && typeof val === "object" && val.sku) {
        // User selected from dropdown — set item_number to the SKU string
        this.product.item_number = val.sku;
      } else if (typeof val === "string") {
        // The combobox may re-emit the display string (e.g. "PC61 — Port &
        // Co™ Essential Tee. PC61") on blur.  Extract just the SKU prefix
        // when the string contains the em-dash separator used by the search
        // endpoint.
        const dashIdx = val.indexOf(" \u2014 ");
        this.product.item_number = dashIdx > 0 ? val.substring(0, dashIdx).trim() : val;
      } else {
        // User cleared the field (val is null/undefined)
        this.product.item_number = "";
      }
    },

    async fetchSkuResults(query) {
      if (this._skuAbortController) {
        this._skuAbortController.abort();
      }
      this._skuAbortController = new AbortController();
      const generation = ++this.skuSearchGeneration;
      this.skuSearchLoading = true;
      try {
        const response = await this.$axios.get(
          `/product/searchSanMarSKU/${encodeURIComponent(query)}`,
          { signal: this._skuAbortController.signal }
        );
        if (generation === this.skuSearchGeneration) {
          this.skuSearchResults = response.data;
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("SKU search failed:", describeError(err));
          if (generation === this.skuSearchGeneration) {
            this.skuSearchResults = [];
          }
        }
      } finally {
        if (generation === this.skuSearchGeneration) {
          this.skuSearchLoading = false;
        }
      }
    },
    async loadVendors() {
      // NOT a `finally { return result.data }` any more (#3252). On a
      // throw `result` was still undefined, so the finally died reading
      // `.data` and replaced the real failure with a TypeError -- the
      // same double masking PR #3247 traced in the checkout path. Raise
      // the real error and rethrow; `mounted` reads `.vendors` off this
      // and would throw on undefined anyway.
      try {
        const result = await this.$axios.post("/vendor/getVendorList", {});
        return result.data;
      } catch (err) {
        this.$store.dispatch('raiseError', err);
        throw err;
      }
    },

    changedTaxCodeLabel() {
      this.productTaxCode = this.getProductTaxCodes[this.productTaxCodeLabel];
      this.product.product_tax_code = this.productTaxCode;
    },

    selectOrCreateProduct() {
      if (typeof this.productSelectorValue === "string") {
        this.product = Product.generateEmpty();
        this.product.name = this.productSelectorValue;

        if (this.initialDistributor) {
          this.selectedDistributor = this.initialDistributor;
        }

        this.$refs.form?.resetValidation();
      } else {
        this.product = this.productSelectorValue;
        this.selectedDistributor = this.product.distributor;
        // this.selectedProductType = this.product.product_type;
      }
    },

    selectProductType() {
      const [selectedProdType] = this.possibleProductTypes.filter(
        (productType) => productType.name === this.productTypeSelectorValue
      );
      this.selectedProductType = selectedProdType;
      this.selectedSupplier = null;
      this.selectedSupplierId = null;
      // changeSupplier is called by the selectedSupplierId watcher
    },

    // async loadProducts() {
    //   let filters = [];

    //   if (this.$store.state.account.role.name === "Sales Executive") {
    //     filters.push({
    //       field: "created_by_id",
    //       op: "==",
    //       value: this.$store.state.account._jv.id,
    //     });
    //   }

    //   if (this.selectedDistributor && this.selectedDistributor._jv.id) {
    //     filters.push({
    //       field: "distributor_id",
    //       op: "==",
    //       value: this.selectedDistributor._jv.id,
    //     });
    //   }

    //   this.productsPending = true;

    //   try {
    //     let products = await this.$store.dispatch("jv/get", [
    //       "products",
    //       {
    //         params: {
    //           filter: JSON.stringify(filters),
    //           include: "distributor,product_type",
    //         },
    //       },
    //     ]);

    //     let productTypeIds = this.possibleProductTypes.map((obj) => obj._jv.id);

    //     this.products = Object.values(products).filter((obj) =>
    //       productTypeIds.includes(obj.product_type_id)
    //     );
    //   } finally {
    //     this.productsPending = false;
    //   }
    // },

    async loadInitialProduct() {
      await this.loadProductTypes();
      await this.loadDistributors();

      this.$data.product = this.$props.initialProduct;

      // Sync selector values that are bound to separate v-models
      this.productSelectorValue = this.product.name;

      if (this.product.distributor) {
        this.selectedDistributor = this.product.distributor;
      } else if (this.product.distributor_id) {
        // figure out which distributor matches the id and set it
        let matchingDistributors = this.possibleDistributors.filter(
          (obj) => obj._jv.id === this.product.distributor_id
        );

        if (matchingDistributors) {
          this.selectedDistributor = matchingDistributors[0];
        } else {
          this.selectedDistributor = null;
        }
      } else {
        this.selectedDistributor = null;
      }

      // Only override selectedProductType if the product has one (edit mode).
      // For new products, keep the value set by loadProductTypes().
      if (this.product.product_type) {
        this.selectedProductType = this.product.product_type;
      }

      // Save the supplier ID for deferred resolution in mounted() after
      // vendors load. Do NOT set selectedSupplierId here — the watcher
      // calls onSupplierIdChanged() which tries to match against
      // possibleSuppliers, but vendors haven't loaded yet so it would
      // null out selectedSupplier and reset the SKU state.
      if (this.product.supplier) {
        const sid = this.product.supplier.id
          || (this.product.supplier._jv && this.product.supplier._jv.id)
          || null;
        if (sid) {
          this._pendingSupplierId = String(sid);
        }
      } else if (this.product.campaign_products) {
        const cps = Object.values(this.product.campaign_products);
        const cpWithSupplier = cps.find((cp) => cp.supplier_id);
        if (cpWithSupplier) {
          this._pendingSupplierId = String(cpWithSupplier.supplier_id);
        }
      }

      this.$refs.form?.resetValidation();
    },

    async loadProductTypes() {
      this.possibleProductTypesPending = true;

      try {
        let filters = [];
        if (this.$props.distributorDisabled) {
          filters.push({
            field: "distributor_id",
            value: this.initialDistributor._jv.id,
          });
        }

        let productTypes = await this.$store.dispatch("jv/get", [
          "product_types",
          {
            params: {
              filter: JSON.stringify(filters),
              include: "primary_attribute,secondary_attribute",
            },
          },
        ]);

        // Get productTypes list optimization; it works well to get productTypes list but we should optimize other parts at the same time. //
        // let distributor_id = "";

        // if (this.$props.distributorDisabled) {
        //   distributor_id = this.initialDistributor._jv.id;
        // }

        // let result = await this.$axios.get(
        //   `/product/getProductTypes/${distributor_id}`
        // );
        // let productTypes = result.data;
        // this.possibleProductTypes = Object.values(productTypes);

        this.possibleProductTypes = Object.values(productTypes);
        // Always pre-select the first product type so downstream computeds
        // (possibleSuppliers, isSmartSupplier) have a valid reference.
        if (this.possibleProductTypes.length > 0 && !this.selectedProductType?.name) {
          this.selectedProductType = this.possibleProductTypes[0];
          this.productTypeSelectorValue = this.possibleProductTypes[0].name;
        }
      } finally {
        this.possibleProductTypesPending = false;
      }
    },

    async loadDistributors() {
      this.possibleDistributorsPending = true;

      try {
        // let distributors = await this.$store.dispatch('jv/get', 'distributors');
        let result = await this.$axios.get(`/campaign/getDistributorsList`);
        let distributors = result.data;
        this.possibleDistributors = Object.values(distributors);
      } finally {
        this.possibleDistributorsPending = false;
      }
    },

    getProduct() {
      // Return a plain object — the product may be a reactive jv store
      // proxy that rejects setting relationship properties directly.

      // For smart suppliers, item_number comes from the SKU combobox
      // (skuSelection), not the hidden text field.
      let itemNumber = this.product.item_number;
      if (this.isSmartSupplier && this.skuSelection) {
        if (typeof this.skuSelection === 'object' && this.skuSelection.sku) {
          itemNumber = this.skuSelection.sku;
        } else if (typeof this.skuSelection === 'string') {
          const dashIdx = this.skuSelection.indexOf(' \u2014 ');
          itemNumber = dashIdx > 0 ? this.skuSelection.substring(0, dashIdx).trim() : this.skuSelection;
        }
      }

      // Resolve supplier_id from multiple sources — the selectedSupplier
      // may come from possibleSuppliers (hoisted id) or from the jv store
      // (only _jv.id). Fall back to selectedSupplierId which is always set.
      let supplierId = null;
      if (this.selectedSupplierId) {
        supplierId = this.selectedSupplierId;
      } else if (this.selectedSupplier) {
        supplierId = this.selectedSupplier._jv?.id || this.selectedSupplier.id || null;
      }

      return {
        name: this.productSelectorValue || this.product.name,
        item_number: itemNumber,
        product_tax_code: this.product.product_tax_code,
        distributor_id: this.selectedDistributor._jv.id,
        product_type_id: this.selectedProductType._jv.id,
        supplier_id: supplierId,
        // Carry relationship objects for the POST path (which builds its own payload)
        product_type: this.selectedProductType,
        supplier: this.selectedSupplier,
        _jv: {
          type: this.product._jv.type,
          id: this.product._jv.id,
        },
      };
    },
  },
};
</script>

<style scoped>
.add-edit-form {
  margin-bottom: 40px;
}
</style>

